import TabShell from "./TabShell";

// Two-panel format comparison (Raw Vial · Pen Kit). Replaces the earlier
// three-way panel after the 2026-08 Vial Kit format-wide withdrawal. The
// convenience benefit Vial Kits carried is retained on the Raw Vial
// panel via the Recon Kit add-on line, the actual toggle lives on the
// Raw Vial PDP.
export default function FormatPanel({ product }) {
  const activeFormat = product?.format === "pen" ? "pen" : "raw";
  const selectedClasses = "border-2 border-brandblue/40 bg-brandbluelight";
  const idleClasses = "border border-line bg-white/70";
  const columns = [
    {
      key: "raw",
      label: "RAW VIAL",
      rows: {
        "Prep required": "Self-reconstitute",
        "Dosing control": "Full researcher control",
        "Shelf life": "Room-temp stable",
        "Best for": "Experienced buyers",
      },
    },
    {
      key: "pen",
      label: "PRE-FILLED PEN",
      rows: {
        "Prep required": "None, ready to use",
        "Dosing control": "Precise, pre-measured",
        "Shelf life": "Refrigerated storage",
        "Best for": "Maximum convenience",
      },
    },
  ];
  const rowKeys = ["Prep required", "Dosing control", "Shelf life", "Best for"];
  return (
    <TabShell testId="format-panel">
      <div className="mx-auto flex w-full max-w-[640px] flex-col">
        <h3
          className="font-heading text-lg font-black leading-tight text-ink sm:text-2xl"
          data-testid="format-panel-headline"
        >
          Two formats. <span className="text-subtle">One catalog.</span>
        </h3>
        <p className="mt-1 text-xs text-subtle sm:mt-2 sm:text-sm">
          Same compound, two ways to dose.
        </p>

        {/* Full-width two-panel grid, no edge padding so the panels sit
            flush against the outer container. Solid backgrounds only,
            no dotted/grid texture. */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:mt-6 sm:gap-4">
          {columns.map((col) => {
            const isActive = activeFormat === col.key;
            return (
              <div
                key={col.key}
                className={`relative flex min-w-0 flex-col rounded-xl p-3 transition-colors sm:p-5 ${
                  isActive ? selectedClasses : idleClasses
                }`}
                data-testid={`format-panel-${col.key}`}
                aria-pressed={isActive}
              >
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex w-fit items-center rounded-md border border-line bg-white/80 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.14em] text-ink sm:px-2 sm:py-1 sm:text-[10px] sm:tracking-[0.16em]">
                    {col.label}
                  </span>
                  {isActive && (
                    <span
                      className="hidden rounded-full bg-brandblue px-2 py-0.5 font-mono text-[9px] tracking-[0.12em] text-white sm:inline-flex"
                      data-testid={`format-panel-${col.key}-selected`}
                    >
                      SELECTED
                    </span>
                  )}
                </div>
                <dl className="mt-3 space-y-2.5 sm:mt-4 sm:space-y-3">
                  {rowKeys.map((k) => (
                    <div key={k}>
                      <dt className="font-mono text-[9px] uppercase tracking-[0.1em] text-subtle sm:text-[10px]">
                        {k}
                      </dt>
                      <dd className="mt-0.5 text-[11px] leading-snug text-ink sm:text-[12px] sm:leading-relaxed">
                        {col.rows[k]}
                      </dd>
                    </div>
                  ))}
                </dl>
                {col.key === "raw" && (
                  <p
                    className="mt-3 rounded-md bg-brandblue/6 px-2 py-1.5 font-mono text-[9px] leading-snug tracking-[0.06em] text-brandbluedark sm:text-[10px]"
                    data-testid="format-panel-raw-recon-note"
                  >
                    + Recon Kit add-on available (BAC water + syringes) for
                    ready-to-draw convenience.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </TabShell>
  );
}
